import React, { useState } from "react";
import "./ApplyCouponModal.css";

const getPromoCodeValue = (coupon = {}) =>
  String(
    coupon.promocode ||
    coupon.promoCode ||
    coupon.promo_code ||
    coupon.promo_code_value ||
    coupon.couponCode ||
    coupon.coupon_code ||
    coupon.code ||
    coupon.voucherCode ||
    coupon.voucher_code ||
    coupon.value ||
    coupon.label ||
    ""
  ).trim();

const ApplyCouponModal = ({ show, onClose, onApply, offers = [], loading = false, message = { type: "", text: "" } }) => {
  const [couponCode, setCouponCode] = useState("");
  const [applyState, setApplyState] = useState({ status: "idle", text: "Apply" });
  const [localMessage, setLocalMessage] = useState(message);
  const applyRequestRef = React.useRef(false);

  React.useEffect(() => {
    setLocalMessage(message);
  }, [message]);

  React.useEffect(() => {
    if (!show) return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousPageOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousPageOverflow;
    };
  }, [show]);

  if (!show) return null;

  const availableCoupons = Array.isArray(offers) ? offers : [];

  const applyCoupon = async (code) => {
    const selectedCode = String(code || "").trim();

    if (!selectedCode || applyRequestRef.current) return;

    applyRequestRef.current = true;
    setApplyState({ status: "pending", text: "Applying..." });

    try {
      const result = await onApply(selectedCode);

      if (result?.success) {
        const successText = result.message || "Applied";
        setApplyState({ status: "success", text: successText });
        setLocalMessage({ type: "success", text: successText });
        setCouponCode("");
        window.setTimeout(() => {
          onClose();
          setApplyState({ status: "idle", text: "Apply" });
        }, 900);
        return;
      }

      const errorText = result?.message || "Unable to apply coupon.";
      setApplyState({ status: "error", text: "Retry" });
      setLocalMessage({ type: "error", text: errorText });
    } catch (error) {
      const errorText =
        (error && (error.message || error.msg || error.error || error.error_message)) ||
        "Unable to apply coupon.";
      setApplyState({ status: "error", text: "Retry" });
      setLocalMessage({ type: "error", text: String(errorText) });
    } finally {
      applyRequestRef.current = false;
    }
  };

  return (
    <div className="coupon-modal-backdrop">
      <div className="coupon-modal" role="dialog" aria-modal="true" aria-label="Coupon">
        <div className="coupon-modal-header">
          <button
            type="button"
            className="coupon-back-btn"
            onClick={onClose}
            aria-label="Close coupon modal"
          >
            <span></span>
          </button>
          <h2>Coupon</h2>
        </div>

        <div className="coupon-modal-content">
          <h3>Apply Coupon</h3>

          {localMessage.text && (
            <div
              style={{
                marginBottom: "16px",
                color: localMessage.type === "error" ? "#d9534f" : "#28a745",
                fontSize: "0.95rem",
                lineHeight: 1.4,
              }}
            >
              {localMessage.text}
            </div>
          )}

          <form
            className="coupon-entry"
            onSubmit={(event) => {
              event.preventDefault();
              applyCoupon(couponCode);
            }}
          >
            <input
              type="text"
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value)}
              placeholder="Please enter a valid coupon code"
            />
            <button type="submit" disabled={["pending", "success"].includes(applyState.status)}>
              {applyState.text}
            </button>
          </form>

          <div className="coupon-list">
            {loading && <p className="mb-0">Loading promo codes...</p>}

            {!loading && availableCoupons.length === 0 && (
              <p className="mb-0">No promo codes available</p>
            )}

            {!loading && availableCoupons.map((coupon, index) => {
              const couponCodeValue = getPromoCodeValue(coupon);

              return (
              <div className="coupon-ticket" key={couponCodeValue || index}>
                <div className="coupon-ticket-header">
                  <h4>{coupon.title || coupon.name || coupon.label || couponCodeValue}</h4>
                </div>

                <div className="coupon-ticket-body">
                  <p>{coupon.description || coupon.message || coupon.detail || coupon.terms}</p>

                  <div className="coupon-ticket-actions">
                    <span className="coupon-code">{couponCodeValue}</span>
                    <button type="button" className="coupon-terms">
                      T&amp;C
                    </button>
                    <button
                      type="button"
                      className="coupon-apply-now"
                      disabled={["pending", "success"].includes(applyState.status)}
                      onClick={() => applyCoupon(couponCodeValue)}
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyCouponModal;
